import {
	type BrightnessCorrectionState,
	type Capture,
	type CollectionFieldAnchor,
	type CollectionFieldAnchorProviderLocation,
	type CollectionFieldFileTool,
	type CollectionFieldHeight,
	type CollectionFieldHeightProviderManual,
	type CollectionFieldHeightProviderPhotomeasure,
	type CollectionFieldHeightProviderPhotomeasureManual,
	type CollectionFieldHeightProviderSpanHeight,
	type CollectionFieldVector,
	type CollectionFieldVectorProviderCollection,
	type CollectionFieldVectorProviderManual,
	type CollectionFieldVectorProviderPointToPoint,
	type CropParameterState,
	type DrawableState,
	DrawableTag,
	type FreeCropParameterState,
	type GammaBrightnessCorrectionState,
	type ImageVisibilityState,
	type LineDrawableState,
	type MarkerDrawableState,
	type MaskDrawableState,
	type VerticalSeparationDrawableState,
} from '@ikegps/core-api';

export const isFreeCropParameter = (o: CropParameterState): o is FreeCropParameterState => o.tag === 'free';
export const isGammaBrightnessCorrection = (o: BrightnessCorrectionState): o is GammaBrightnessCorrectionState =>
	o.tag === 'gamma';
export const isHiddenImageVisibility = (o: ImageVisibilityState): o is ImageVisibilityState => o.tag === 'hidden';
export const isMarkerDrawable = (o: DrawableState): o is MarkerDrawableState =>
	o.tag === DrawableTag.MARKER || o.tag === DrawableTag.OFFSET;
export const isLineDrawable = (o: DrawableState): o is LineDrawableState => o.tag === DrawableTag.LINE;
export const isMaskDrawable = (o: DrawableState): o is MaskDrawableState => o.tag === DrawableTag.MASK;
export const isVerticalSeparationDrawable = (o: DrawableState): o is VerticalSeparationDrawableState =>
	o.tag === DrawableTag.VERTICAL_SEPARATION;
export const isVectorPointToPointProvider = (
	fieldData: CollectionFieldVector
): fieldData is CollectionFieldVectorProviderPointToPoint => fieldData.provider === 'pointtopoint';
export const isVectorCollectionProvider = (
	fieldData: CollectionFieldVector
): fieldData is CollectionFieldVectorProviderCollection => fieldData.provider === 'collection';
export const isVectorProviderManual = (
	fieldData: CollectionFieldVector
): fieldData is CollectionFieldVectorProviderManual => fieldData.provider === 'manual';
export const isHeightSpanHeightProvider = (
	fieldData: CollectionFieldHeight
): fieldData is CollectionFieldHeightProviderSpanHeight => fieldData.provider === 'spanheight';
export const isHeightPhotomeasureProvider = (
	fieldData: CollectionFieldHeight
): fieldData is CollectionFieldHeightProviderPhotomeasure => fieldData.provider === 'photomeasure';
export const isHeightPhotomeasureManualProvider = (
	fieldData: CollectionFieldHeight
): fieldData is CollectionFieldHeightProviderPhotomeasureManual => fieldData.provider === 'photomeasuremanual';
export const isHeightProviderManual = (
	fieldData: CollectionFieldHeight
): fieldData is CollectionFieldHeightProviderManual => fieldData.provider === 'manual';
export const isLink = (fieldData: CollectionFieldVector): fieldData is CollectionFieldVector =>
	fieldData.provider === 'collection';
export const isCaptureData = (object): object is Capture => object.camera != null && object.camera.imageuuid != null;
export const isFileData = (object: CollectionFieldFileTool): object is CollectionFieldFileTool =>
	object.attachmentId != null && object.size != null;
export const isAnchorLocationGPS = (object: CollectionFieldAnchor): object is CollectionFieldAnchorProviderLocation =>
	object.value.collectionTool === 'LOCATION' && object.provider === 'gps';
export const isAnchorP2P = (object: CollectionFieldAnchor): object is CollectionFieldAnchorProviderLocation =>
	object.value.collectionTool === 'VECTOR' && object.provider === 'pointtopoint';
export const isAnchorManual = (object: CollectionFieldAnchor): object is CollectionFieldAnchorProviderLocation =>
	object.value.collectionTool === 'VECTOR' && object.provider === 'manual';

export function findObjectsWhere(data, pred: (obj) => boolean) {
	if (pred(data)) {
		return [data];
	}

	let found = [];
	for (const key of Object.keys(data)) {
		const value = data[key];
		if (value && typeof value === 'object') {
			found = found.concat(findObjectsWhere(value, pred));
		}
	}
	return found;
}
